import type { Id, Project } from '../../api/types';

/**
 * PROJECT MEMBERSHIP, SEEN FROM THE USER — PH-56's second half.
 *
 * 🔴 THE TICKET: Or asked to *"let admin manage which project they are in also"*. The API for it has
 * existed all along and the web app called NONE of it:
 *     GET    /projects/:id/members            ✅ exists
 *     POST   /projects/:id/members            ✅ exists (admin)
 *     DELETE /projects/:id/members/:userId    ✅ exists (admin)
 * A grep for `/members` across the web app returns exactly one line, and that line is a COMMENT.
 *
 * ⭐⭐ THE API IS PROJECT-CENTRIC AND THE QUESTION IS USER-CENTRIC, SO THE CLIENT INVERTS IT. There
 * is no `GET /users/:id/projects`. Or's question — "which projects is this person in" — can only be
 * answered by reading every project's member list and turning it inside out. That is what
 * `invertMemberships` does.
 * ⚠️ AND THAT IS A REAL COST, NOT A DETAIL: it is N requests for N projects (9 today). It is
 * acceptable at this size and it would not be at 200. When it stops being acceptable the fix is an
 * endpoint, not a cleverer client — written here so the next person meets the decision rather than
 * the symptom.
 *
 * ⛔ WHY THE PER-USER VIEW AND NOT THE PER-PROJECT ONE. Both are useful and the per-project one is
 * the obvious build. But placing a new joiner is the actual job, and per-project makes that NINE
 * screens — which is exactly the friction that left four humans in no project at all today. Or asked
 * for the user-centric one by name.
 */

export interface ProjectMember {
  user_id: Id;
  role: 'member' | 'lead';
  /** Present on some responses; never relied on for identity. */
  display_name?: string | null;
  email?: string | null;
}

/** One project a user belongs to, ready to render. */
export interface UserProject {
  project: Project;
  role: 'member' | 'lead';
}

/**
 * Turn `project -> members` into `user -> projects`.
 *
 * ⭐ IDS ARE COMPARED AS STRINGS. They are Postgres bigints and arrive as strings; a `==` against a
 * number would work today and break the first time one exceeds 2^53. The whole codebase keeps them
 * as strings end to end, and this is one of the few places two of them meet.
 */
export function invertMemberships(
  projects: readonly Project[],
  membersByProjectId: ReadonlyMap<string, readonly ProjectMember[]>,
): Map<string, UserProject[]> {
  const byUser = new Map<string, UserProject[]>();

  for (const project of projects) {
    const members = membersByProjectId.get(String(project.id)) ?? [];
    for (const member of members) {
      const key = String(member.user_id);
      const list = byUser.get(key);
      const entry: UserProject = { project, role: member.role };
      if (list) list.push(entry);
      else byUser.set(key, [entry]);
    }
  }

  // ⭐ Sorted by project KEY so the same person renders identically on every load. Map iteration
  //   order follows the projects array, which follows whatever the API happened to return.
  for (const list of byUser.values()) {
    list.sort((a, b) => a.project.key.localeCompare(b.project.key));
  }
  return byUser;
}

/**
 * The projects a user is NOT in — the "add to project" options.
 *
 * ⛔ COMPUTED FROM THE SAME SOURCE AS THE MEMBERSHIP LIST, never from a separate fetch. Two fetches
 * would let the picker offer a project the user was added to a moment ago, and adding twice is a
 * conflict the API rightly refuses.
 */
export function projectsAvailableTo(
  projects: readonly Project[],
  current: readonly UserProject[],
): Project[] {
  const taken = new Set(current.map((entry) => String(entry.project.id)));
  return projects.filter((project) => !taken.has(String(project.id)));
}

/**
 * ⚠️ THE REFUSAL THE TICKET ASKED TO GET RIGHT RATHER THAN DISCOVER: removing the LAST LEAD leaves a
 * project nobody but a global admin can administer.
 *
 * ⭐ IT RETURNS A WARNING, NOT A VETO, AND THAT IS THE DELIBERATE CHOICE. A hard block would strand
 * a project whose only lead has left the company — the admin would have to add a lead before
 * removing one, on a screen that exists precisely because people leave. So the rule is: say plainly
 * what is about to become true, and let an admin decide. ⛔ A silent removal is what is refused,
 * not the removal itself.
 */
export function lastLeadWarning(
  members: readonly ProjectMember[],
  removingUserId: Id,
): string | null {
  const target = members.find((m) => String(m.user_id) === String(removingUserId));
  if (!target || target.role !== 'lead') return null;
  const otherLeads = members.filter(
    (m) => m.role === 'lead' && String(m.user_id) !== String(removingUserId),
  );
  if (otherLeads.length > 0) return null;
  return 'This is the only lead on this project. After removing them, no one but a global admin can administer it.';
}
